import { Machine } from "./Machine";
import { TOOL_TYPES, ToolId } from "./Tool";

/** Why a tool can't hang on a station's rack right now. */
export type ToolMountRefusal = "full" | "incompatible" | "working";

/**
 * The one rule for hanging a tool on a station's rack, shared by the
 * command that does it (`mountTool`), the bench view's rail, which
 * lights a hook only when the tool being carried would actually hang
 * there, and the tool-first offer, which reads a tool in hand as if it
 * hung here (`toolOperationFor`): a free hook, a tool made for this
 * kind of station, and no job running (a new tool re-picks the selected
 * operation, which would cancel the running one out from under the
 * stock).
 *
 * @returns why the tool can't be hung right now, or null when it can.
 */
export function toolMountRefusalKind(
  machine: Machine,
  toolId: ToolId,
): ToolMountRefusal | null {
  if (machine.state.tools.length >= machine.toolSlots) return "full";
  const compatible = TOOL_TYPES[toolId].compatibleMachines;
  if (compatible && !compatible.includes(machine.state.machineTypeId)) {
    return "incompatible";
  }
  if (machine.state.operationProgress.status === "inProgress") {
    return "working";
  }
  return null;
}

/** `toolMountRefusalKind`, worded for the log. */
export function toolMountRefusal(
  machine: Machine,
  toolId: ToolId,
): string | null {
  switch (toolMountRefusalKind(machine, toolId)) {
    case "full":
      return `No free tool slots on ${machine.type.name}`;
    case "incompatible":
      return `${toolId} doesn't mount on a ${machine.type.name}`;
    case "working":
      return "Can't mount tools while the station is working";
    case null:
      return null;
  }
}
